import {
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Icons,
  Input,
  List,
  Select,
  Skeleton,
  Space,
  Spin,
  Tabs,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { Grid } from 'antd';
import { EStatus } from 'enums';
import { useDebounce } from 'hooks';
import { IClient } from 'interfaces';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { getCategoriesByWarehouse } from 'services/category';
import { getClient } from 'services/client';
import { getRestaurantPosInventoryByWarehouse } from 'services/inventory';
import { SearchIndex, getSearchData } from 'services/meilisearch';
// import { CreateSaleData, createSale } from 'services/sale';
import { useCan, useNavigation } from '@pankod/refine-core';
import SelectionButtons from 'components/SelectionButtons';
import { RiUser2Line } from 'react-icons/ri';
import { CreateRestaurantPosPayload, EOrderType, createRestaurantPos } from 'services/pos';
import { getTransactionToken } from 'services/transaction';
import { getWarehousesDropdown } from 'services/warehouse';
import MetaData from 'utilities/metadata';
import { showsVat } from 'utilities/sale-vat';
import { formatCurrency, getAddressAsString } from 'utilities/shared';
import styles from './styles.module.css';

interface IWarehouseAndCategory {
  id: string;
  name: string;
}

interface ICartItem {
  id: string;
  name: string;
  sku: string;
  url: string | null;
  description: string;
  unitPrice: number;
  totalUnit: number;
  quantity: number;
}

interface IOption {
  value: string;
  label: string | React.ReactNode;
}

enum ActionKind {
  SetLoading = 'SET_LOADING',
  SetWarehouses = 'SET_WAREHOUSES',
  SetSelectedWarehouse = 'SET_SELECTED_WAREHOUSE',
  SetCategories = 'SET_CATEGORIES',
  SetCartOptions = 'SET_CART_OPTIONS',
  IncreaseCartItemQuantity = 'INCREASE_CART_ITEM_QUANTITY',
  DecreaseCartItemQuantity = 'DECREASE_CART_ITEM_QUANTITY',
  AddItem = 'ADD_ITEM',
  RemoveItem = 'REMOVE_ITEM',
  EmptyCart = 'EMPTY_CART',
}

type Action =
  | { type: ActionKind.SetLoading; payload: boolean }
  | { type: ActionKind.SetWarehouses; payload: IWarehouseAndCategory[] }
  | { type: ActionKind.SetSelectedWarehouse; payload: string }
  | { type: ActionKind.SetCategories; payload: IWarehouseAndCategory[] }
  | { type: ActionKind.SetCartOptions; payload: ICartItem[] }
  | { type: ActionKind.IncreaseCartItemQuantity; payload: string }
  | { type: ActionKind.DecreaseCartItemQuantity; payload: string }
  | { type: ActionKind.AddItem; payload: string }
  | { type: ActionKind.RemoveItem; payload: string }
  | { type: ActionKind.EmptyCart };

interface IState {
  isLoading: boolean;
  warehouses: IWarehouseAndCategory[];
  selectedWarehouse?: string;
  categories: IWarehouseAndCategory[];
  cartOptions: ICartItem[];
  cart: ICartItem[];
}

const initialState: IState = {
  isLoading: false,
  warehouses: [],
  selectedWarehouse: undefined,
  categories: [],
  cartOptions: [],
  cart: [],
};

const posReducer = (state: IState, action: Action) => {
  switch (action.type) {
    case ActionKind.SetLoading:
      return { ...state, isLoading: action.payload };
    case ActionKind.SetWarehouses:
      return { ...state, warehouses: action.payload };
    case ActionKind.SetSelectedWarehouse:
      return { ...state, selectedWarehouse: action.payload };
    case ActionKind.SetCategories:
      return { ...state, categories: action.payload };
    case ActionKind.SetCartOptions:
      return { ...state, cartOptions: action.payload };
    case ActionKind.AddItem:
      // Get item quantity from option
      const option = state.cartOptions.find((item) => item.id === action.payload);
      if (!option) return { ...state };

      // update quantity or add item based on item already exist or not
      const isItemExistInCart = !!state.cart.find((item) => item.id === option.id);
      if (isItemExistInCart) {
        return {
          ...state,
          cart: state.cart.map((item) => {
            if (item.id === option.id) {
              return {
                ...item,
                quantity: item.quantity + 1,
              };
            } else {
              return { ...item };
            }
          }),
        };
      } else {
        return {
          ...state,
          cart: [...state.cart, { ...option, quantity: 1 }],
        };
      }
    case ActionKind.RemoveItem:
      return {
        ...state,
        cart: state.cart.filter((item) => item.id !== action.payload),
      };
    case ActionKind.EmptyCart:
      return {
        ...state,
        cart: [],
      };
    case ActionKind.IncreaseCartItemQuantity:
      return {
        ...state,
        cart: state.cart.map((item) => {
          // if (item.id === action.payload && item.quantity < item.totalUnit) {
          if (item.id === action.payload) {
            return {
              ...item,
              quantity: item.quantity + 1,
            };
          }
          return item;
        }),
      };
    case ActionKind.DecreaseCartItemQuantity:
      const product = state.cart.find((product) => product.id === action.payload);
      if (!product) return { ...state };

      if (product.quantity === 1) {
        return {
          ...state,
          cart: state.cart.filter((product) => product.id !== action.payload),
        };
      } else {
        return {
          ...state,
          cart: state.cart.map((item) => {
            if (item.id === action.payload && item.quantity > 1) {
              return {
                ...item,
                quantity: item.quantity - 1,
              };
            }
            return item;
          }),
        };
      }
    default:
      return state;
  }
};

const TAX = 0;

const OrderTypeOptions = [
  {
    id: EOrderType.DineIn,
    title: 'Dine-In',
  },
  {
    id: EOrderType.Takeaway,
    title: 'Parcel',
  },
];

export const RestaurantPos = () => {
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.sm;
  const productImageSize = isMobile ? 96 : 120;
  const [productSearchValue, setProductSearchValue] = useState<string>('');
  const [{ isLoading, warehouses, selectedWarehouse, categories, cartOptions, cart }, dispatch] =
    useReducer(posReducer, initialState);
  const [filteredCartOptions, setFilteredCartOptions] = useState<ICartItem[]>([]);
  const [customer, setCustomer] = useState<{
    isSearching: boolean;
    isFetching: boolean;
    searchValue: string | null;
    options: IOption[];
    selected: IClient | null;
    newCustomer: { name: string; number: string } | null;
  }>({
    isSearching: false,
    isFetching: false,
    searchValue: null,
    options: [],
    selected: null,
    newCustomer: null,
  });
  // const [discountAmount, setDiscountAmount] = useState<number>(0);
  // const [cashIn, setCashIn] = useState<number>(0);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const newCustomerNumberRef = useRef<string>();
  // const [newCustomerName, setNewCustomerName] = useState<string>('');
  const [isCheckOut, setIsCheckOut] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const debouncedCustomerSearchValue = useDebounce(customer.searchValue, 500);
  const debouncedProductSearchValue = useDebounce(productSearchValue, 500);
  const { push } = useNavigation();
  const { data: canReadStaff } = useCan({
    resource: 'staff',
    action: 'list',
  });
  const [orderType, setOrderType] = useState<EOrderType>(EOrderType.DineIn);
  const [tabActiveKey, setTabActiveKey] = useState<string>('1');
  // const filteredCartOptions = !debouncedProductSearchValue.trim()
  //   ? cartOptions
  //   : cartOptions.filter((item) => {
  //       const mergedValue = `${item.sku.toLowerCase()}::${item.name.toLowerCase()}`;
  //       return mergedValue.includes(productSearchValue.toLowerCase());
  //     });
  const subtotal = cart.reduce((total, item) => {
    total += item.unitPrice * item.quantity;
    return total;
  }, 0);
  const subtotalTaxAmount = subtotal * TAX;
  const totalAmount = subtotal + subtotalTaxAmount;
  // const totalPayableAmount = totalAmount - discountAmount;
  // const returnAmount = cashIn === 0 ? 0 : cashIn - totalPayableAmount;
  // const isCartEmpty = cart.length === 0;
  // const isPaymentConfirmationDisabled =
  //   cashIn < totalPayableAmount || isPaymentLoading || !cart.length;
  const isPaymentConfirmationDisabled = isPaymentLoading || !cart.length;

  useEffect(() => {
    if (cart.length < 1) return;

    window.onbeforeunload = function (e) {
      e.preventDefault();
      return 'Changes you made may not be saved.';
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [cart.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: ActionKind.SetLoading, payload: true });
        const warehouseResponse = await getWarehousesDropdown();
        const warehouses = warehouseResponse.map((warehouse) => ({
          id: warehouse.id,
          name: warehouse.name,
        }));

        const selectedWarehouseId = warehouses?.[0]?.id;

        dispatch({ type: ActionKind.SetWarehouses, payload: warehouses });
        dispatch({ type: ActionKind.SetSelectedWarehouse, payload: selectedWarehouseId });

        if (selectedWarehouseId) {
          await changeWarehouseSelectionCallback(selectedWarehouseId);
        }
      } catch (error: any) {
        notification.error({
          message: 'Error Fetching Data',
          description: error.message,
        });
        dispatch({ type: ActionKind.SetWarehouses, payload: [] });
      } finally {
        dispatch({ type: ActionKind.SetLoading, payload: false });
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (!debouncedCustomerSearchValue || !debouncedCustomerSearchValue.trim()) {
        return;
      }

      try {
        setCustomer((prev) => ({ ...prev, isSearching: true, options: [] }));
        let response = await getSearchData(SearchIndex.Client, debouncedCustomerSearchValue);
        // exact match after character length of 10
        if (debouncedCustomerSearchValue.length > 10) {
          response = response.filter((item) =>
            item.mobileNumber.includes(debouncedCustomerSearchValue)
          );
        }
        // When there is no response show new customer card
        if (!response.length) {
          newCustomerNumberRef.current = debouncedCustomerSearchValue;
          setCustomer((prev) => ({ ...prev, isSearching: true, selected: null }));
        }

        const options = response.map((item) => ({
          value: item.client_id,
          label: (
            <Typography.Text key={item.client_id}>
              {item.name} - {item.mobileNumber}
            </Typography.Text>
          ),
        }));
        setCustomer((prev) => ({ ...prev, isSearching: false, options }));
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCustomerSearchValue]);

  useEffect(() => {
    if (!selectedWarehouse) return;

    const options = !debouncedProductSearchValue.trim()
      ? cartOptions
      : cartOptions.filter((item) => {
        const mergedValue = `${item.sku.toLowerCase()}::${item.name.toLowerCase()}`;
        return mergedValue.includes(productSearchValue.toLowerCase());
      });

    setFilteredCartOptions(options);
    setSelectedCategory(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedProductSearchValue]);

  const changeWarehouseSelectionCallback = useCallback(async (warehouseId: string) => {
    try {
      dispatch({ type: ActionKind.SetLoading, payload: true });
      dispatch({ type: ActionKind.SetCartOptions, payload: [] });
      dispatch({ type: ActionKind.EmptyCart });
      dispatch({ type: ActionKind.SetSelectedWarehouse, payload: warehouseId });

      const [variantResponse, categoryResponse] = await Promise.all([
        getRestaurantPosInventoryByWarehouse({ warehouseId }),
        getCategoriesByWarehouse(warehouseId),
      ]);

      const variants = variantResponse.map((variant) => ({
        ...variant,
        totalUnit: variant.quantity,
        quantity: 0,
      }));

      dispatch({ type: ActionKind.SetCategories, payload: categoryResponse });
      dispatch({
        type: ActionKind.SetCartOptions,
        payload: variants,
      });
      setFilteredCartOptions(variants);
    } catch (err: any) {
      notification.error({
        message: 'Error Fetching Data',
        description: err.message,
      });
    } finally {
      dispatch({ type: ActionKind.SetLoading, payload: false });
    }
  }, []);

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;

    if (cart.length === 0) {
      changeWarehouseSelectionCallback(warehouseId);
    } else if (cart.length > 0 && window.confirm('Changes you made may not be saved.')) {
      changeWarehouseSelectionCallback(warehouseId);
    } else {
      return;
    }
  };

  const getOptions = (items: IWarehouseAndCategory[]) => {
    return items.map((item) => {
      return (
        <Select.Option key={item.id} value={item.id}>
          {item.name}
        </Select.Option>
      );
    });
  };

  const handleCustomerSelect = async (value: string) => {
    try {
      setCustomer((prev) => ({
        ...prev,
        selected: null,
        isFetching: true,
        searchValue: '',
        newCustomer: null,
      }));
      newCustomerNumberRef.current = undefined;
      const client = await getClient(value);
      setCustomer((prev) => ({ ...prev, selected: client }));
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: 'Failed to get client.',
      });
    } finally {
      setCustomer((prev) => ({ ...prev, isFetching: false }));
    }
  };

  const handleCategorySelect = async (categoryId: string | null = null) => {
    try {
      dispatch({ type: ActionKind.SetLoading, payload: true });
      // dispatch({ type: ActionKind.SetCartOptions, payload: [] });
      setSelectedCategory(categoryId);
      setProductSearchValue('');

      const variantResponse = await getRestaurantPosInventoryByWarehouse({
        warehouseId: selectedWarehouse!,
        categoryId: categoryId,
      });

      const variants = variantResponse.map((variant) => ({
        ...variant,
        totalUnit: variant.quantity,
        quantity: 0,
      }));

      setFilteredCartOptions(variants);

      // dispatch({
      //   type: ActionKind.SetCartOptions,
      //   payload: variantResponse.map((variant) => ({
      //     ...variant,
      //     totalUnit: variant.quantity,
      //     quantity: 0,
      //   })),
      // });
    } catch (err: any) {
      notification.error({
        message: 'Error Fetching Data',
        description: err.message,
      });
    } finally {
      dispatch({ type: ActionKind.SetLoading, payload: false });
    }
  };

  const handleEmptyCart = () => {
    dispatch({ type: ActionKind.EmptyCart });
    setCustomer((prev) => ({ ...prev, selected: null, newCustomer: null }));
    // setDiscountAmount(0);
    // setCashIn(0);
  };

  const handlePayment = async () => {
    const saleData: CreateRestaurantPosPayload = {
      client: null,
      newClientDetails: null,
      salesDate: new Date(),
      salesStatus: EStatus.CONFIRMED,
      warehouse: selectedWarehouse!,
      salesAddress: 'Dhaka, Bangladesh, 1000',
      vatAmount: 0,
      // discountCost: discountAmount,
      discountCost: 0,
      salesPayments: [],
      salesProducts: cart.map((product) => ({
        sku: product.sku,
        quantity: product.quantity,
        unitPrice: product.unitPrice,
        salesProductStatus: EStatus.CONFIRMED,
      })),
      transactionToken: await getTransactionToken(),
      orderType,
    };
    if (customer.selected !== null) {
      saleData.client = customer.selected.id;
      saleData.salesAddress = getAddressAsString(customer.selected.address) as string;
    }
    if (customer.newCustomer !== null) {
      saleData.newClientDetails = {
        name: customer.newCustomer.name,
        mobileNumber: customer.newCustomer.number,
        countryCode: '+88',
      };
    }

    return await createRestaurantPos(saleData);
  };

  const handleConfirmPayment = async () => {
    if (!selectedWarehouse) return;

    setIsPaymentLoading(true);
    try {
      await handlePayment();

      notification.success({
        message: 'Success',
        description: 'Successfully created a order.',
      });
      handleEmptyCart();
      setIsCheckOut(false);
      setSelectedCategory(null);
      setTabActiveKey('1');
      if (selectedWarehouse) {
        changeWarehouseSelectionCallback(selectedWarehouse);
      }
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsPaymentLoading(false);
  };

  const pageHeader = (
    <Space>
      {canReadStaff?.can && (
        <Button
          type="primary"
          onClick={() => push('/staff')}
          icon={<RiUser2Line style={{ paddingBottom: '2px' }} size={16} />}
          style={{ display: 'flex', alignItems: 'center', gap: 2 }}
        >
          My Orders
        </Button>
      )}
    </Space>
  );

  if (isCheckOut) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '12px 1rem',
          backgroundColor: '#fff',
          position: 'relative',
        }}
      >
        <Typography.Title level={3}>
          <Button
            // type="text"
            icon={<Icons.ArrowLeftOutlined />}
            style={{ marginRight: '6px', marginBottom: '4px' }}
            onClick={() => setIsCheckOut(false)}
          />
          Transaction
        </Typography.Title>
        <Divider style={{ marginTop: 0 }} />

        <Select
          showSearch
          loading={customer.isSearching}
          value={customer.searchValue}
          placeholder="Search by customer phone number, name, code"
          defaultActiveFirstOption={false}
          filterOption={false}
          onSearch={(value: string) => {
            if (!!value.trim()) {
              setCustomer((prev) => ({ ...prev, searchValue: value, options: [] }));
            } else {
              setCustomer((prev) => ({ ...prev, searchValue: null, options: [] }));
            }
          }}
          onSelect={handleCustomerSelect}
          notFoundContent={null}
          style={{ width: '100%' }}
          options={customer.options}
        />
        {/* {!!newCustomerNumberRef.current && !!newCustomerNumberRef.current.trim() && (
          <Card
            style={{
              marginTop: '0.8rem',
              backgroundColor: '#F7F9FC',
            }}
          >
            <Typography.Text>{newCustomerNumberRef.current} - Not Found</Typography.Text>
            <div
              style={{
                marginTop: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <Input
                type="text"
                placeholder="Add customer name"
                style={{ width: '85%' }}
                onChange={(e) => {
                  const text = e.target.value;
                  setNewCustomerName(text);
                }}
              />
              <Button
                icon={<Icons.UserAddOutlined />}
                disabled={!newCustomerName.trim()}
                onClick={() => {
                  if (!!newCustomerNumberRef.current && !!newCustomerName.trim()) {
                    setCustomer((prev) => ({
                      ...prev,
                      selected: null,
                      newCustomer: {
                        name: newCustomerName,
                        number: newCustomerNumberRef.current!,
                      },
                    }));
                    newCustomerNumberRef.current = undefined;
                    setNewCustomerName('');
                  }
                }}
              />
            </div>
          </Card>
        )} */}

        {customer.isFetching && (
          <Skeleton
            paragraph={{ rows: 1 }}
            style={{
              marginTop: '0.8rem',
              backgroundColor: '#F7F9FC',
              padding: '12px',
              borderRadius: '6px',
            }}
          />
        )}
        {customer.newCustomer === null && !customer.isFetching && customer.selected && (
          <Card size="small" style={{ marginTop: '0.8rem', backgroundColor: '#F7F9FC' }}>
            <Typography.Text
              type="success"
              style={{ display: 'inline-block', marginBottom: '0.5rem' }}
            >
              Customer Found
            </Typography.Text>
            <br />
            <Typography.Text strong style={{ fontSize: '1rem' }}>
              {customer.selected.name}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary">{customer.selected.mobileNumber}</Typography.Text>
          </Card>
        )}
        {/* {customer.selected === null && customer.newCustomer !== null && (
          <Card size="small" style={{ marginTop: '0.8rem', backgroundColor: '#F7F9FC' }}>
            <Typography.Text
              type="success"
              style={{ display: 'inline-block', marginBottom: '0.5rem' }}
            >
              New Customer
            </Typography.Text>
            <br />
            <Typography.Text strong style={{ fontSize: '1rem' }}>
              {customer.newCustomer.name}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary">{customer.newCustomer.number}</Typography.Text>
          </Card>
        )} */}

        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {cart.map((item) => {
            return (
              <Card bodyStyle={{ padding: '1rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 20,
                  }}
                >
                  <div style={{ flex: 1, display: 'flex', gap: 5 }}>
                    {!!item.url ? (
                      <Avatar
                        size={60}
                        shape="square"
                        style={{ objectFit: 'cover' }}
                        src={item.url}
                        alt={`Avatar`}
                      />
                    ) : (
                      <Avatar
                        size={60}
                        shape="square"
                        style={{ objectFit: 'cover' }}
                        icon={<Icons.ShoppingCartOutlined />}
                        alt={`Avatar`}
                      />
                    )}

                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        paddingTop: '2px',
                      }}
                    >
                      <Typography.Text title={item.name}>
                        {item.name.length > 30 ? item.name.substring(0, 30) + '…' : item.name}
                      </Typography.Text>

                      <Typography.Text type="secondary">
                        TK {item.unitPrice} x {item.quantity}
                      </Typography.Text>
                    </div>
                  </div>

                  <Typography.Text strong>TK {item.quantity * item.unitPrice}</Typography.Text>
                </div>
              </Card>
            );
          })}
        </div>

        <Divider style={{ margin: '1rem 0', backgroundColor: '#000' }} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography.Text strong>Subtotal</Typography.Text>
            <Typography.Text>TK {formatCurrency(subtotal)}</Typography.Text>
          </div>

          {showsVat(TAX * 100, subtotalTaxAmount) && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text strong>Tax</Typography.Text>
              <Typography.Text>TK {formatCurrency(subtotalTaxAmount)}</Typography.Text>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography.Text strong>Total</Typography.Text>
            <Typography.Text>TK{formatCurrency(totalAmount)}</Typography.Text>
          </div>
        </div>

        {/* <div style={{ marginTop: '1rem' }}>
          <Typography.Text>Discount</Typography.Text>
          <Input
            prefix="TK"
            type="number"
            min={0}
            value={discountAmount === 0 ? '' : discountAmount}
            disabled={isCartEmpty}
            onChange={(e) => {
              const value = !!e.target.value ? e.target.value : '0';
              const parsedValue = isNaN(Number.parseFloat(value))
                ? 0
                : Math.abs(Number.parseFloat(value));
              const discount = parsedValue >= subtotal ? 0 : parsedValue;
              setDiscountAmount(discount);
            }}
            style={{ width: '100%' }}
          />
        </div> */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem 0',
          }}
        >
          <Typography.Text>Payable Amount</Typography.Text>
          <Typography.Text strong style={{ fontSize: '1.5rem' }}>
            TK {formatCurrency(totalAmount)}
          </Typography.Text>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <SelectionButtons
            options={OrderTypeOptions}
            value={orderType}
            onChange={(selectedId) => setOrderType(selectedId as EOrderType)}
          />
        </div>

        {/* <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '1rem',
          }}
        >
          <div style={{ flexBasis: '50%' }}>
            <Typography.Text>Cash Paid</Typography.Text>
            <Input
              prefix="TK"
              type="number"
              min={0}
              value={cashIn === 0 ? '' : cashIn}
              disabled={isCartEmpty}
              onChange={(e) => {
                const value = !!e.target.value ? e.target.value : '0';
                const parsedValue = isNaN(Number.parseFloat(value))
                  ? 0
                  : Math.abs(Number.parseFloat(value));
                setCashIn(parsedValue);
              }}
            />
          </div>

          <div style={{ flexBasis: '50%' }}>
            <Typography.Text>Return</Typography.Text>
            <p
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                backgroundColor: '#D6FFE1',
              }}
            >
              TK {returnAmount <= 0 ? '' : returnAmount}
            </p>
          </div>
        </div> */}

        <div
          style={{
            marginTop: 'auto',
            width: '100%',
            marginBottom: '1rem',
            position: 'sticky',
            bottom: 10,
          }}
        >
          <Button
            type="primary"
            size="large"
            loading={isPaymentLoading}
            disabled={isPaymentConfirmationDisabled}
            onClick={handleConfirmPayment}
            style={{ width: '100%' }}
          >
            Confirm Order &rarr;
          </Button>
        </div>
      </div>
    );
  }

  return (
    <List headerProps={{ extra: pageHeader }}>
      <MetaData />
      {/* Menus & Order */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          // padding: '1rem',
          minHeight: 'calc(100vh - 80px)',
          backgroundColor: '#fff',
        }}
      >
        <Select
          showSearch
          placeholder="Select Location"
          loading={isLoading}
          disabled={isLoading || !warehouses.length}
          value={selectedWarehouse}
          onSelect={(value: string) => onSelectWarehouse(value?.toString())}
          filterOption={(input, option: any) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          size="large"
          style={{ width: '100%' }}
        >
          {getOptions(warehouses)}
        </Select>

        <Tabs
          activeKey={tabActiveKey}
          size="large"
          style={{ flex: 1 }}
          onChange={(activeKey) => setTabActiveKey(activeKey)}
        >
          {/* MENU */}
          <Tabs.TabPane tab="Menu" key="1">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                height: '75vh',
              }}
            >
              {/* Categories */}
              {selectedWarehouse && filteredCartOptions.length ? (
                <div
                  style={{
                    maxWidth: '100vw',
                    overflowX: 'scroll',
                    display: 'flex',
                    padding: '1rem 0',
                    gap: '5px',
                    scrollbarWidth: 'none',
                  }}
                >
                  <Button
                    key="All"
                    type={selectedCategory === null ? 'primary' : 'text'}
                    onClick={() => {
                      handleCategorySelect(null);
                    }}
                  >
                    All
                  </Button>
                  {categories.map((category) => (
                    <Button
                      key={category.id}
                      type={selectedCategory === category.id ? 'primary' : 'text'}
                      onClick={() => {
                        handleCategorySelect(category.id);
                      }}
                    >
                      {category.name}
                    </Button>
                  ))}
                </div>
              ) : null}

              <Input
                type="text"
                size="large"
                placeholder="Search by Item Name or SKU"
                disabled={!selectedWarehouse || isLoading}
                value={productSearchValue}
                onChange={(e) => setProductSearchValue(e.target.value)}
                style={{ width: '100%' }}
              />

              {isLoading && (
                <Space
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                  }}
                >
                  <Spin size="large" tip="Loading..." />
                </Space>
              )}

              <div
                style={{ flex: 1, overflowY: 'scroll', padding: '16px', height: '100%' }}
                className={styles['custom-scrollbar']}
              >
                {filteredCartOptions.map((product) => {
                  const productInCart = cart.find((p) => p.id === product.id) ?? null;

                  return (
                    <div
                      key={product.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        paddingTop: '1rem',
                        paddingBottom: '1.5rem',
                        borderBottom: '1px solid #ccc',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <Typography.Text
                          style={{
                            fontSize: '1rem',
                            display: 'block',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            maxWidth: '100%',
                          }}
                        >
                          {product.name}
                        </Typography.Text>
                        <Typography.Text strong style={{ fontSize: '1.2rem' }}>
                          Tk. {product.unitPrice}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            textAlign: 'justify',
                            textJustify: 'none',
                            fontSize: '0.8rem',
                          }}
                          type="secondary"
                        >
                          {product.description !== null && product.description.length > 120
                            ? product.description.substring(0, 120) + '...'
                            : product.description}
                        </Typography.Text>
                      </div>

                      <div
                        style={{
                          alignSelf: 'flex-end',
                          position: 'relative',
                          flexShrink: 0,
                          width: productImageSize,
                        }}
                      >
                        {productInCart === null ? (
                          <Button
                            type="primary"
                            icon={<Icons.PlusCircleFilled />}
                            onClick={() => {
                              // if (product.totalUnit === 0) {
                              //   return notification.warn({
                              //     message: 'Out of stock!',
                              //     description: 'Please adjust your quantity.',
                              //   });
                              // }
                              dispatch({ type: ActionKind.AddItem, payload: product.id });
                            }}
                            style={{
                              position: 'absolute',
                              zIndex: '5',
                              bottom: '5px',
                              right: '5px',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              position: 'absolute',
                              zIndex: 5,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '6px',
                              bottom: '8px',
                              right: '5px',
                              background: '#fff',
                              padding: '0 8px',
                              borderRadius: '12px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              return;
                            }}
                          >
                            <Button
                              icon={<Icons.MinusOutlined />}
                              type="text"
                              size="small"
                              onClick={() => {
                                dispatch({
                                  type: ActionKind.DecreaseCartItemQuantity,
                                  payload: product.id,
                                });
                              }}
                            />
                            <Typography.Text>{productInCart.quantity}</Typography.Text>
                            <Button
                              icon={<Icons.PlusOutlined />}
                              type="text"
                              size="small"
                              onClick={() => {
                                // if (productInCart.quantity + 1 > productInCart.totalUnit) {
                                //   return notification.warn({
                                //     message: 'Out of stock!',
                                //     description: 'Please adjust your quantity.',
                                //   });
                                // }
                                dispatch({
                                  type: ActionKind.IncreaseCartItemQuantity,
                                  payload: product.id,
                                });
                              }}
                            />
                          </div>
                        )}

                        {!!product.url ? (
                          <Avatar
                            size={productImageSize}
                            shape="square"
                            style={{ objectFit: 'cover' }}
                            src={product.url}
                            alt={`Avatar`}
                          />
                        ) : (
                          <Avatar
                            size={productImageSize}
                            shape="square"
                            style={{ objectFit: 'cover' }}
                            icon={<Icons.ShoppingCartOutlined />}
                            alt={`Avatar`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {cart.length ? (
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={() => setTabActiveKey('2')}
                    style={{
                      position: 'fixed',
                      bottom: 12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 'calc(100% - 32px)', // Adjust for padding
                      zIndex: 10,
                    }}
                  >
                    Go to Order &rarr;
                  </Button>
                ) : null}
              </div>
            </div>
          </Tabs.TabPane>

          {/* ORDER ITEMS */}
          <Tabs.TabPane
            tab={cart.length ? `Ordered Items (${cart.length})` : 'Ordered Items'}
            key="2"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
              }}
            >
              {isLoading && (
                <Space
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                  }}
                >
                  <Spin size="large" tip="Loading..." />
                </Space>
              )}

              {isLoading === false && cart.length === 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography.Text
                    style={{ marginTop: '5rem', fontSize: '2rem', textAlign: 'center' }}
                  >
                    You haven’t placed any orders yet.
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    Browse products and make your first order!
                  </Typography.Text>
                </div>
              )}

              {isLoading === false && cart.length > 0 && (
                <div>
                  {cart.map((product) => {
                    return (
                      <div
                        key={product.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '1rem',
                          paddingTop: '1rem',
                          paddingBottom: '1.5rem',
                          borderBottom: '1px solid #ccc',
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}
                        >
                          <Typography.Text
                            style={{
                              fontSize: '1rem',
                              display: 'block',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              maxWidth: '100%',
                            }}
                          >
                            {product.name}
                          </Typography.Text>
                          <Typography.Text strong style={{ fontSize: '1.2rem' }}>
                            Tk. {product.unitPrice}
                          </Typography.Text>
                          <Typography.Text
                            style={{
                              textAlign: 'justify',
                              textJustify: 'none',
                              fontSize: '0.8rem',
                            }}
                            type="secondary"
                          >
                            {product.description !== null && product.description.length > 120
                              ? product.description.substring(0, 120) + '...'
                              : product.description}
                          </Typography.Text>
                        </div>

                        <div
                          style={{
                            alignSelf: 'flex-end',
                            position: 'relative',
                            flexShrink: 0,
                            width: productImageSize,
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              zIndex: 5,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '6px',
                              bottom: '8px',
                              right: '5px',
                              background: '#fff',
                              padding: '0 8px',
                              borderRadius: '12px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              return;
                            }}
                          >
                            <Button
                              icon={<Icons.MinusOutlined />}
                              type="text"
                              size="small"
                              onClick={() => {
                                dispatch({
                                  type: ActionKind.DecreaseCartItemQuantity,
                                  payload: product.id,
                                });
                              }}
                            />
                            <Typography.Text>{product.quantity}</Typography.Text>
                            <Button
                              icon={<Icons.PlusOutlined />}
                              type="text"
                              size="small"
                              onClick={() => {
                                dispatch({
                                  type: ActionKind.IncreaseCartItemQuantity,
                                  payload: product.id,
                                });
                              }}
                            />
                          </div>

                          <Badge
                            size="default"
                            count={
                              <Button
                                type="text"
                                icon={<Icons.CloseCircleFilled />}
                                onClick={() => {
                                  dispatch({
                                    type: ActionKind.RemoveItem,
                                    payload: product.id,
                                  });
                                }}
                              />
                            }
                            style={{ color: '#f5222d', cursor: 'pointer' }}
                          >
                            {!!product.url ? (
                              <Avatar
                                size={productImageSize}
                                shape="square"
                                style={{ objectFit: 'cover' }}
                                src={product.url}
                                alt={`Avatar`}
                              />
                            ) : (
                              <Avatar
                                size={productImageSize}
                                shape="square"
                                style={{ objectFit: 'cover' }}
                                icon={<Icons.ShoppingCartOutlined />}
                                alt={`Avatar`}
                              />
                            )}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setIsCheckOut(true)}
                    style={{ width: '100%', marginTop: 'auto' }}
                  >
                    Checkout &rarr;
                  </Button>
                </div>
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </List>
  );
};
